import React, { useMemo } from 'react';
import { Drawer, IconButton, Button } from '@mui/material';
import { Close, Delete, Remove, Add, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import './Cart.styled.scss';
import { TCartComponent } from './Cart.types';
import { APP_ROUTES } from '../../../constants/routes';
import { useLocalCart } from '../../../hooks/useLocalCart';

export const Cart: TCartComponent = ({ open, onClose }) => {
  const navigate = useNavigate();
  const {
    items: cartItems,
    total: subtotal,
    updateQuantity,
    removeItem,
  } = useLocalCart();

  const FREE_SHIPPING_THRESHOLD = 50;
  const freeShippingUnlocked = subtotal >= FREE_SHIPPING_THRESHOLD;
  const freeShippingProgress = Math.min(
    (subtotal / FREE_SHIPPING_THRESHOLD) * 100,
    100
  );
  const remainingForFreeShipping = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - subtotal
  );

  const handleQuantityChange = (itemId: string, delta: number) => {
    const item = cartItems.find((item) => item.id === itemId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + delta);
      updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
  };

  const formatValues = (values: Record<string, string>): string => {
    return Object.entries(values)
      .map(([key, value]) => `${key}: ${value}`)
      .join(' / ');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="cart-drawer"
      PaperProps={{
        className: 'cart-drawer__paper',
      }}
    >
      <div className="cart">
        {/* Header */}
        <div className="cart__header">
          <h2>MY CART</h2>
          <IconButton
            onClick={onClose}
            className="cart__close-button"
            aria-label="close cart"
          >
            <Close />
          </IconButton>
        </div>

        <div className="cart__content">
          {/* Free Shipping Banner */}
          {freeShippingUnlocked ? (
            <div className="cart__banner cart__banner--success">
              <p>Congrats! You have unlocked free standard shipping!</p>
              <div className="cart__progress-bar">
                <div
                  className="cart__progress-bar-fill"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          ) : (
            <div className="cart__banner cart__banner--info">
              <p>
                Add {remainingForFreeShipping.toFixed(2)} zl. more for free
                standard shipping!
              </p>
              <div className="cart__progress-bar">
                <div
                  className="cart__progress-bar-fill"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Cart Items */}
          <div className="cart__items">
            {cartItems.length === 0 ? (
              <div className="cart__empty">
                <p>Your cart is empty</p>
              </div>
            ) : (
              cartItems.map((item) => (
                <div key={item.id} className="cart__item">
                  <div className="cart__item-image">
                    <img src={item.mainImage} alt={item.title} />
                  </div>
                  <div className="cart__item-details">
                    <h4 className="cart__item-title">{item.title}</h4>
                    <div className="cart__item-meta">
                      <span className="cart__item-variant">
                        {Object.keys(item.values).length > 0
                          ? formatValues(item.values)
                          : 'Standard'}
                        <IconButton size="small" className="cart__edit-button">
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </div>
                    <div className="cart__item-footer">
                      <div className="cart__item-price">
                        <span>
                          {(item.pricePerItem * item.quantity).toFixed(2)} zl.
                        </span>
                      </div>
                      <div className="cart__item-controls">
                        <div className="cart__quantity-control">
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(item.id, -1)}
                            className="cart__quantity-button"
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <span className="cart__quantity-value">
                            {item.quantity}
                          </span>
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(item.id, 1)}
                            className="cart__quantity-button"
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </div>
                        <IconButton
                          onClick={() => handleRemoveItem(item.id)}
                          className="cart__delete-button"
                          aria-label="remove item"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="cart__footer">
          <div className="cart__footer-content">
            <div className="cart__subtotal">
              <h3>SUBTOTAL: {subtotal.toFixed(2)} zl.</h3>
            </div>
          </div>
          <Button
            variant="contained"
            fullWidth
            className="cart__checkout-button"
            disabled={cartItems.length === 0}
            onClick={() => {
              onClose();
              navigate(APP_ROUTES.CHECKOUT);
            }}
          >
            START CHECKOUT →
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
